/**
 * The handful of primitives Phase 2's screens need.
 *
 * Deliberately not fifteen components. The roadmap's component floor is written for a phase with
 * screens to build them against; components invented before their screens are components built to
 * the wrong shape, and vendoring shadcn/ui wholesale now would mean reviewing a hundred files
 * nothing imports. These five exist because the auth and admin screens in this phase use them.
 *
 * Every one styles through SEMANTIC tokens. Tailwind's stock palette is cleared in theme.css, so
 * `bg-blue-600` does not compile — that half of the rule is enforced by the build rather than by
 * review. The other half is not: a MISSPELT semantic name (`border-border`, `bg-surface`) compiles
 * to nothing at all, silently, which is why `infra/verify-design-tokens.mjs` exists.
 */
import { useState } from 'react'
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react'
import { Icon } from './icons'

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  readonly variant?: 'primary' | 'secondary' | 'danger'
}

const BUTTON_BASE =
  'inline-flex h-control items-center justify-center rounded-sm px-3 font-medium disabled:cursor-not-allowed disabled:opacity-50'

const BUTTON_VARIANT: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'bg-accent text-text-on-accent hover:bg-accent-hover',
  secondary: 'border border-border-strong bg-surface-raised text-text-primary',
  danger: 'border border-danger bg-surface-raised text-danger',
}

export function Button({ variant = 'primary', className = '', type, ...rest }: ButtonProps) {
  return (
    <button
      // Explicit, because an unset `type` inside a form is `submit`, and a "Cancel" that submits
      // the form is the kind of bug that only shows up in the one flow nobody re-tested.
      type={type ?? 'button'}
      className={`${BUTTON_BASE} ${BUTTON_VARIANT[variant]} ${className}`}
      {...rest}
    />
  )
}

export type FieldProps = InputHTMLAttributes<HTMLInputElement> & {
  readonly label: string
  readonly hint?: ReactNode
  readonly error?: string
  /**
   * `stacked` (the default) puts the label above the input. `horizontal` puts the label and its
   * hint in a left column and the input in a right one, from `sm` up, and stacks below that.
   *
   * Opt-in rather than a global change, and that is deliberate. The ceremony and the editor are
   * narrow columns where a label beside a control would leave the control too small to type a
   * signer's name into; the settings pages are a wide reading surface where a stacked label makes
   * every row twice as tall as it needs to be and pushes the Save button below the fold. One
   * layout cannot be right for both, so the caller says which it is.
   */
  readonly layout?: 'stacked' | 'horizontal'
}

/**
 * A field's one-sentence hint, folded behind a small "?" beside the label.
 *
 * ## Why it exists
 *
 * The founder, 2026-09-16, from a screenshot of the Brand page: "the field description should be
 * displayed as tooltip only." On a `horizontal` field the hint used to sit UNDER the input, in the
 * input's column — and beside a Save button that column is a third of the card, so a sentence
 * like "A hex value, or a CSS colour like rgb(51 65 204)" wrapped into a tower eleven lines tall
 * next to a one-line input. The layout was right for the input and wrong for the prose.
 *
 * ## What it must not lose
 *
 * The hint is the input's `aria-describedby`, and that is the part a tooltip usually breaks: a
 * sentence that exists only while a mouse hovers a glyph reaches nobody on a phone and nobody
 * using a screen reader (the `Switch` in controls.tsx records the same lesson about `title`). So
 * the text is ALWAYS in the DOM, visually hidden, under the id the input points at — a screen
 * reader hears it on focus exactly as before — and the visible bubble is an extra way to read it,
 * not the only one.
 *
 * Opens on hover and on keyboard focus, so a keyboard user who tabs to the glyph reads it; and on
 * click/tap, because a touch screen has neither. Escape closes it and returns focus nowhere it was
 * not already. The glyph is a real button with an accessible name, never a bare span with a
 * handler: an unnamed control is announced as "button" and nothing else.
 */
export function HintTip({
  label,
  hintId,
  children,
}: {
  /** The field the hint is about, for the glyph's accessible name. */
  readonly label: string
  /** The id the field's `aria-describedby` names; the visually hidden copy carries it. */
  readonly hintId: string
  readonly children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  return (
    // Escape on the WRAPPER rather than a document listener, and no outside-click listener at all.
    // Both were tried and both cost bundle: `HintTip` lives in the design system, which every route
    // carries, and this component is 318 bytes gzipped as it stands (measured with `lint:bundle` by
    // building with and without it — enough to put the editor route over the 150 KB budget it had
    // at the time, which ADR 0127 has since raised to 200 KB for its own reasons). What the two
    // listeners bought is already covered: focus leaving the glyph closes it (`onBlur`), tapping the
    // glyph again toggles it, and Escape works wherever focus is — which, for a control only
    // reachable by hovering or focusing it, is inside this wrapper.
    <span
      className="relative inline-flex"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false)
      }}
    >
      {/* The copy every reader gets: the input's description, whether or not the bubble is up. */}
      <span id={hintId} className="sr-only">
        {children}
      </span>
      <button
        type="button"
        aria-label={`About ${label}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="text-text-muted hover:text-text-primary focus-visible:ring-accent inline-flex size-5 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2"
      >
        <Icon name="help" size={14} />
      </button>
      {open ? (
        <span
          role="tooltip"
          className="border-border-subtle bg-surface-raised text-text-primary shadow-overlay absolute start-0 top-full z-20 mt-1 w-64 rounded-md border p-2 text-sm font-normal"
        >
          {children}
        </span>
      ) : null}
    </span>
  )
}

export function Field({
  label,
  hint,
  error,
  id,
  className = '',
  layout = 'stacked',
  ...rest
}: FieldProps) {
  const inputId = id ?? `field-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
  const hintId = hint ? `${inputId}-hint` : undefined
  const errorId = error ? `${inputId}-error` : undefined
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined

  const input = (
    <input
      id={inputId}
      // Both wired: aria-invalid alone gives a screen reader "invalid" with no reason, and the
      // error text alone is invisible to it entirely.
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy}
      className={`h-control bg-field-bg text-text-primary w-full rounded-sm border px-3 ${
        error ? 'border-danger' : 'border-border-strong'
      } ${className}`}
      {...rest}
    />
  )

  if (layout === 'horizontal') {
    return (
      // `items-start`, not `items-center`: a wrapped error on the right must not drag the label.
      //
      // The hint is a tooltip beside the label here, not a paragraph under the input (`HintTip`
      // says why). It keeps its id, so the input's `aria-describedby` still resolves to the text.
      <div className="grid gap-x-4 gap-y-1 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] sm:items-start">
        <span className="inline-flex items-center gap-1.5 pt-2">
          <label htmlFor={inputId} className="text-text-primary font-medium">
            {label}
          </label>
          {hint && hintId ? (
            <HintTip label={label} hintId={hintId}>
              {hint}
            </HintTip>
          ) : null}
        </span>
        <div className="flex flex-col gap-1">
          {input}
          {error ? (
            <p id={errorId} role="alert" className="text-danger m-0 text-sm">
              {error}
            </p>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputId} className="text-text-primary font-medium">
        {label}
      </label>
      {hint ? (
        <p id={hintId} className="text-text-muted m-0 text-sm">
          {hint}
        </p>
      ) : null}
      {input}
      {error ? (
        <p id={errorId} role="alert" className="text-danger m-0 text-sm">
          {error}
        </p>
      ) : null}
    </div>
  )
}

export type CardProps = {
  readonly title?: string
  /** One clause under the title, when the title alone does not say what the card is for. */
  readonly description?: string
  /** A control on the title's right — an "Edit", a "View all". */
  readonly action?: ReactNode
  readonly children: ReactNode
  readonly className?: string
}

/**
 * The console's one container.
 *
 * `rounded-xl` + `shadow-raised` rather than a bare hairline border: on a page of eight cards the
 * border alone leaves everything on one plane, and the eye has nothing to sort by. One step of
 * elevation is enough to say "this is a thing" — and because it comes from the token layer it
 * inverts correctly in the dark, where a light-mode shadow would simply disappear.
 *
 * The header row exists so a card with an action does not have to rebuild a flex row and get the
 * baseline slightly wrong; when there is no title and no action it renders nothing at all.
 */
export function Card({ title, description, action, children, className = '' }: CardProps) {
  const header = title !== undefined || action !== undefined
  return (
    <section
      className={`border-card-border bg-card-bg shadow-raised rounded-xl border p-5 ${className}`}
    >
      {header ? (
        <div className="mb-4 flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
          <div className="min-w-0">
            {title !== undefined ? (
              <h2 className="text-text-primary m-0 text-base font-semibold tracking-tight">
                {title}
              </h2>
            ) : null}
            {description !== undefined ? (
              <p className="text-text-muted m-0 mt-0.5 text-sm">{description}</p>
            ) : null}
          </div>
          {action !== undefined ? (
            <div className="shrink-0 text-sm font-medium">{action}</div>
          ) : null}
        </div>
      ) : null}
      {children}
    </section>
  )
}

export type BadgeProps = {
  readonly tone: 'neutral' | 'success' | 'warning' | 'danger'
  /**
   * `soft` is a tinted pill and the default: a status is a category, and a tinted ground sorts
   * eight rows into four groups from across a desk in a way four shades of small text do not.
   * `outline` is the old hairline form, kept for the places a badge sits inside another chip and a
   * second filled shape would read as two states rather than one.
   */
  readonly variant?: 'soft' | 'outline'
  /** A leading dot in the tone's colour — for status, where the word alone carries no urgency. */
  readonly dot?: boolean
  /**
   * Pulse, for a state that is IN FLIGHT and that somebody is waiting on — a document still in the
   * conversion queue, where a static badge reads as a settled answer and invites the next click too
   * early (the founder, 2026-09-15: "when file is there in Queue, make sure its in red color and
   * blinking").
   *
   * `motion-reduce:animate-none` is not a softening of that: a thing that flashes and cannot be
   * stopped is WCAG 2.3.1, and this product commits to 2.2 AA (`ux.md` §5). Somebody who has asked
   * their operating system for less motion gets the same red badge, standing still.
   */
  readonly pulsing?: boolean
  readonly children: ReactNode
}

const BADGE_SOFT: Record<BadgeProps['tone'], string> = {
  // 12% rather than 10%: the amber and green here are dark, AA-passing 600s, and at 10% the amber
  // ground is indistinguishable from the neutral one on a calibrated laptop at arm's length.
  neutral: 'bg-surface-sunken text-text-muted',
  success: 'bg-success/12 text-success',
  warning: 'bg-warning/12 text-warning',
  danger: 'bg-danger/12 text-danger',
}

const BADGE_OUTLINE: Record<BadgeProps['tone'], string> = {
  neutral: 'border-border-subtle border text-text-muted',
  success: 'border-border-subtle border text-success',
  warning: 'border-border-subtle border text-warning',
  danger: 'border-border-subtle border text-danger',
}

const DOT_TONE: Record<BadgeProps['tone'], string> = {
  neutral: 'bg-text-muted',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
}

export function Badge({
  tone,
  variant = 'soft',
  dot = false,
  pulsing = false,
  children,
}: BadgeProps) {
  // Joined from a LIST rather than interpolated. The pulse arrived as `}${pulsing ? 'animate-…'}`
  // with its leading space inside the template, and a reformat ate the space — which silently
  // produced `text-dangeranimate-pulse`, a class that styles nothing. A `toContain` assertion could
  // not see it either; the test below uses `classList`, which tokenises on whitespace.
  const classes = [
    'inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium',
    variant === 'soft' ? BADGE_SOFT[tone] : BADGE_OUTLINE[tone],
    ...(pulsing ? ['animate-pulse motion-reduce:animate-none'] : []),
  ]
  return (
    <span className={classes.join(' ')}>
      {dot ? (
        <span aria-hidden="true" className={`size-1.5 shrink-0 rounded-full ${DOT_TONE[tone]}`} />
      ) : null}
      {children}
    </span>
  )
}

export type CalloutProps = {
  readonly tone: 'info' | 'warning' | 'danger'
  readonly title: string
  readonly children: ReactNode
}

const CALLOUT_TONE: Record<CalloutProps['tone'], string> = {
  info: 'border-border-strong',
  warning: 'border-warning',
  danger: 'border-danger',
}

/** For the statements a user must read before acting — region permanence, session revocation. */
export function Callout({ tone, title, children }: CalloutProps) {
  return (
    <div className={`bg-surface-sunken rounded-md border-l-2 p-4 ${CALLOUT_TONE[tone]}`}>
      <p className="text-text-primary m-0 font-medium">{title}</p>
      <div className="text-text-muted mt-1">{children}</div>
    </div>
  )
}
